import React, { useState, useEffect, useRef, useCallback } from 'react';
import { RefreshCw, MapPin, Globe2, Building2, HelpCircle, X, Phone, Mail } from 'lucide-react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import toast from 'react-hot-toast';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { fetchBuyerLocations, fetchBuyerLocationDetail } from '../api/analyticsApi';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';
const MAP_STYLE = 'mapbox://styles/mapbox/light-v11';
const DEFAULT_CENTER = { lng: 134.0, lat: -25.0 }; // roughly centre of Australia
const BAR_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#6366F1'];

const toNum = (v) => (v === null || v === undefined ? NaN : Number(v));

/* ── Stat card ─────────────────────────────────────────────────────────────── */
const StatCard = ({ Icon, label, value, sub, accent }) => (
    <div className="flex items-center gap-3 bg-white rounded-xl border border-gray-200 p-4">
        <span
            className="flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0"
            style={{ background: `${accent}1a`, color: accent }}
        >
            <Icon className="w-5 h-5" />
        </span>
        <div className="min-w-0">
            <div className="text-2xl font-bold text-gray-900 leading-tight">{value}</div>
            <div className="text-xs text-gray-500">{label}</div>
            {sub && <div className="text-[11px] text-gray-400 mt-0.5 truncate">{sub}</div>}
        </div>
    </div>
);

/* ── Horizontal bar list (clickable) ──────────────────────────────────────── */
const BarList = ({ items, color = '#3B82F6', onItemClick, emptyLabel = 'No data yet' }) => {
    if (!items || items.length === 0) {
        return <div className="text-sm text-gray-400 py-6 text-center">{emptyLabel}</div>;
    }
    const max = Math.max(1, ...items.map((i) => i.count || 0));
    return (
        <div className="space-y-2">
            {items.map((item, idx) => (
                <button
                    key={`${item.label}-${idx}`}
                    onClick={() => onItemClick?.(item)}
                    className="flex items-center gap-3 w-full text-left group"
                >
                    <span className="text-xs text-gray-600 w-28 sm:w-36 truncate flex-shrink-0" title={item.label}>
                        {item.label || '—'}
                    </span>
                    <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                        <div
                            className="h-4 rounded-full transition-all group-hover:opacity-80"
                            style={{ width: `${(item.count / max) * 100}%`, background: color }}
                        />
                    </div>
                    <span className="text-xs font-semibold text-gray-700 w-12 text-right flex-shrink-0">
                        {(item.count || 0).toLocaleString()}
                    </span>
                </button>
            ))}
        </div>
    );
};

/* ── Map of buyer cities ──────────────────────────────────────────────────── */
function BuyerLocationMap({ points, onCityClick }) {
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const markersRef = useRef([]);
    const [mapReady, setMapReady] = useState(false);
    const [mapError, setMapError] = useState(null);
    const clickRef = useRef(onCityClick);
    clickRef.current = onCityClick;

    useEffect(() => {
        if (!containerRef.current || !MAPBOX_TOKEN) return undefined;
        mapboxgl.accessToken = MAPBOX_TOKEN;
        setMapError(null);
        const container = containerRef.current;
        const map = new mapboxgl.Map({
            container,
            style: MAP_STYLE,
            center: [DEFAULT_CENTER.lng, DEFAULT_CENTER.lat],
            zoom: 3.2,
        });
        mapRef.current = map;
        const resize = () => { try { map.resize(); } catch { /* ignore */ } };
        const ro = new ResizeObserver(resize);
        ro.observe(container);
        const onLoad = () => { resize(); setMapReady(true); };
        const onErr = (e) => setMapError(e?.error?.message || 'Map failed to load');
        map.on('load', onLoad);
        map.on('error', onErr);
        return () => {
            ro.disconnect();
            map.off('load', onLoad);
            map.off('error', onErr);
            map.remove();
            mapRef.current = null;
            setMapReady(false);
        };
    }, []);

    useEffect(() => {
        const map = mapRef.current;
        if (!mapReady || !map) return;
        markersRef.current.forEach((m) => m.remove());
        markersRef.current = [];

        const coords = (points || []).filter(
            (p) => Number.isFinite(toNum(p.longitude)) && Number.isFinite(toNum(p.latitude)),
        );
        if (coords.length === 0) return;

        const maxCount = Math.max(1, ...coords.map((p) => p.count || 0));
        coords.forEach((p) => {
            const lng = toNum(p.longitude);
            const lat = toNum(p.latitude);
            // Circle sized by relative buyer count (16–46px).
            const size = 16 + Math.round((Math.sqrt(p.count || 1) / Math.sqrt(maxCount)) * 30);
            const el = document.createElement('div');
            el.style.cssText = `width:${size}px;height:${size}px;border-radius:9999px;background:rgba(59,130,246,0.55);border:2px solid #2563EB;cursor:pointer;display:flex;align-items:center;justify-content:center;color:#fff;font-size:10px;font-weight:700;`;
            if ((p.count || 0) >= 1) el.textContent = p.count > 999 ? '999+' : String(p.count);
            const marker = new mapboxgl.Marker({ element: el })
                .setLngLat([lng, lat])
                .setPopup(
                    new mapboxgl.Popup({ offset: size / 2, closeButton: false }).setHTML(
                        `<div style="font-size:12px"><strong>${p.city || 'Unknown'}</strong>${p.region ? `, ${p.region}` : ''}<br/>${(p.count || 0).toLocaleString()} buyer${p.count === 1 ? '' : 's'}</div>`,
                    ),
                )
                .addTo(map);
            el.addEventListener('mouseenter', () => marker.togglePopup());
            el.addEventListener('mouseleave', () => marker.togglePopup());
            el.addEventListener('click', (e) => {
                e.stopPropagation();
                clickRef.current?.(p);
            });
            markersRef.current.push(marker);
        });

        const bounds = new mapboxgl.LngLatBounds();
        coords.forEach((p) => bounds.extend([toNum(p.longitude), toNum(p.latitude)]));
        try { map.fitBounds(bounds, { padding: 60, maxZoom: 11 }); } catch { /* ignore */ }
    }, [mapReady, points]);

    if (!MAPBOX_TOKEN) {
        return (
            <div className="h-[360px] rounded-xl border border-dashed border-gray-300 flex items-center justify-center text-sm text-gray-400 text-center px-6">
                Map unavailable — set REACT_APP_MAPBOX_ACCESS_TOKEN to show the buyer location map.
            </div>
        );
    }
    return (
        <div className="relative">
            <div ref={containerRef} className="h-[360px] rounded-xl overflow-hidden border border-gray-200" />
            {mapError && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/80 text-sm text-red-500 rounded-xl">
                    {mapError}
                </div>
            )}
        </div>
    );
}

/* ── Drill-down modal ─────────────────────────────────────────────────────── */
const BuyerListModal = ({ open, onClose, title, rows, loading }) => {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
            <div
                className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-4 border-b border-gray-100">
                    <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-blue-500" /> {title}
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="overflow-y-auto p-2">
                    {loading ? (
                        <div className="flex items-center justify-center py-10 text-gray-400">
                            <RefreshCw className="w-5 h-5 animate-spin" />
                        </div>
                    ) : (rows && rows.length) ? (
                        rows.map((r) => (
                            <div key={r.id} className="px-3 py-2 rounded-lg hover:bg-gray-50">
                                <div className="text-sm font-medium text-gray-900">{r.name}</div>
                                <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500 mt-0.5">
                                    {r.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{r.email}</span>}
                                    {r.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{r.phone}</span>}
                                    <span className="flex items-center gap-1">
                                        <MapPin className="w-3 h-3" />
                                        {[r.city, r.region, r.country].filter(Boolean).join(', ')}
                                    </span>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-sm text-gray-400 py-10 text-center">No buyers found.</div>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ── Panel ────────────────────────────────────────────────────────────────── */
const LocationAnalyticsPanel = ({ startDate, endDate }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const loadTokenRef = useRef(0);

    const [modal, setModal] = useState({ open: false, title: '', rows: [], loading: false });

    useEffect(() => {
        const myToken = ++loadTokenRef.current;
        setLoading(true);
        fetchBuyerLocations(startDate, endDate)
            .then((res) => { if (myToken === loadTokenRef.current) setData(res); })
            .catch(() => { if (myToken === loadTokenRef.current) toast.error('Failed to load location analytics'); })
            .finally(() => { if (myToken === loadTokenRef.current) setLoading(false); });
    }, [startDate, endDate]);

    const openDrill = useCallback(async ({ level, value, region, title }) => {
        setModal({ open: true, title, rows: [], loading: true });
        try {
            const res = await fetchBuyerLocationDetail({ level, value, region, startDate, endDate });
            setModal((m) => ({ ...m, rows: res.rows || [], loading: false }));
        } catch {
            toast.error('Failed to load buyers');
            setModal((m) => ({ ...m, loading: false }));
        }
    }, [startDate, endDate]);

    if (loading && !data) {
        return (
            <div className="flex items-center justify-center py-20 text-gray-400">
                <RefreshCw className="w-6 h-6 animate-spin" />
            </div>
        );
    }

    const d = data || {};
    const topState = d.by_state?.[0];
    const topCity = d.by_city?.[0];
    const stateChart = (d.by_state || []).slice(0, 8).map((s) => ({ name: s.label, count: s.count }));
    const countryChart = (d.by_country || []).slice(0, 8);

    return (
        <div className="space-y-6">
            {/* Headline numbers */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    Icon={MapPin}
                    accent="#3B82F6"
                    value={(d.located ?? 0).toLocaleString()}
                    label="Buyers located"
                    sub={`${d.located_pct ?? 0}% of ${(d.total_buyers ?? 0).toLocaleString()} active buyers`}
                />
                <StatCard
                    Icon={Building2}
                    accent="#10B981"
                    value={topCity ? topCity.label : '—'}
                    label="Top city"
                    sub={topCity ? `${topCity.count.toLocaleString()} buyers` : 'No data yet'}
                />
                <StatCard
                    Icon={Globe2}
                    accent="#8B5CF6"
                    value={topState ? topState.label : '—'}
                    label="Top state / region"
                    sub={topState ? `${topState.count.toLocaleString()} buyers` : 'No data yet'}
                />
                <StatCard
                    Icon={HelpCircle}
                    accent="#9CA3AF"
                    value={(d.unresolved ?? 0).toLocaleString()}
                    label="Location unknown"
                    sub="No IP / not yet resolved"
                />
            </div>

            {/* Map */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h2 className="text-lg font-bold text-gray-900 mb-1">Where buyers are based</h2>
                <p className="text-xs text-gray-500 mb-4">
                    One marker per city, sized by buyer count. Click a marker for the list of buyers there.
                </p>
                <BuyerLocationMap
                    points={d.map_points || []}
                    onCityClick={(p) =>
                        openDrill({
                            level: 'city',
                            value: p.city,
                            region: p.region,
                            title: `Buyers in ${[p.city, p.region].filter(Boolean).join(', ')}`,
                        })
                    }
                />
            </div>

            {/* State + city breakdowns */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <h3 className="text-sm font-bold text-gray-900 mb-4">Buyers by state / region</h3>
                    {stateChart.length ? (
                        <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={stateChart} layout="vertical" margin={{ left: 8, right: 16 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                                <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
                                <Tooltip cursor={{ fill: '#f3f4f6' }} />
                                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                                    {stateChart.map((_, i) => (
                                        <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="text-sm text-gray-400 py-10 text-center">No data yet</div>
                    )}
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <h3 className="text-sm font-bold text-gray-900 mb-4">Top cities</h3>
                    <BarList
                        items={(d.by_city || []).slice(0, 12)}
                        color="#10B981"
                        onItemClick={(c) =>
                            openDrill({
                                level: 'city',
                                value: c.label,
                                region: c.region,
                                title: `Buyers in ${[c.label, c.region].filter(Boolean).join(', ')}`,
                            })
                        }
                    />
                </div>
            </div>

            {/* Countries */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h3 className="text-sm font-bold text-gray-900 mb-4">Buyers by country</h3>
                <BarList
                    items={countryChart}
                    color="#8B5CF6"
                    onItemClick={(c) =>
                        openDrill({
                            level: 'country',
                            value: c.code || c.label,
                            title: `Buyers in ${c.label}`,
                        })
                    }
                />
            </div>

            <BuyerListModal
                open={modal.open}
                onClose={() => setModal((m) => ({ ...m, open: false }))}
                title={modal.title}
                rows={modal.rows}
                loading={modal.loading}
            />
        </div>
    );
};

export default LocationAnalyticsPanel;
